import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'
import { v4 as uuidv4 } from 'uuid'
import { parsePhoneNumberWithError } from 'libphonenumber-js'
import OrganizationSection from './OrganizationSection'
import './OrganizationSplitModal.css'
import { createOrganizations } from './contactEnrichmentUtils'
import {
	decomposeDomainScope,
	assembleScope,
	pathDepthOf,
} from './domainScope'
import { markets } from '../../lib/markets'

// What the domain's scope picker works on: the violation URL it decomposes into
// clickable parts, and the domain that scope is carved out of. Frozen when the
// domain is created — picking a scope reassigns violations, so deriving the
// source from the current ones would make the chips move under the user.
const scopeSource = (violation, baselineDomain, lockBaselinePath) => ({
	sourceUrl: violation ? violation.urlRaw || violation.url || '' : '',
	baselineDomain: baselineDomain || '',
	lockBaselinePath: !!lockBaselinePath,
})

// A domain already holding violations takes its chips from the one reaching
// deepest — a shallower URL would just offer less to carve.
const deepestViolation = (violations) =>
	violations.reduce(
		(deepest, violation) =>
			pathDepthOf(violation.urlRaw || violation.url || '') >
			pathDepthOf(deepest.urlRaw || deepest.url || '')
				? violation
				: deepest,
		violations[0],
	)

// A domain split out of a violation starts on the most specific scope that URL
// offers — the picker is then used to widen it.
const domainFromViolation = (violation, baselineDomain) => {
	// Split off an existing domain, so the part that domain already covers is
	// fixed — this card can only be more specific than the one it came from.
	const source = scopeSource(violation, baselineDomain, true)
	const decomposition = decomposeDomainScope({ name: '', ...source })
	const name = decomposition
		? assembleScope(
				decomposition,
				decomposition.subdomainLabels.length,
				decomposition.pathSegments.length,
			)
		: source.sourceUrl

	return {
		id: uuidv4(),
		name,
		...source,
		violations: [],
		nameValidationError: name ? null : 'Domain name cannot be empty',
	}
}

class DigitalPropertySplitModal extends Component {
	constructor(props) {
		super(props)

		const { organization } = props

		// Build domains from the organization
		const orgDomains =
			organization.domains && organization.domains.length > 0
				? organization.domains
				: [{ domainName: organization.companyName || 'domain', digitalPropertyId: null }]

		// Create a single initial org linked to the original organization
		const initialOrg = {
			id: uuidv4(),
			// The organization this card was loaded from. Never reassigned — what
			// happens to it on save is decided by saveMode alone.
			organizationId: organization.organizationId || null,
			loadedCompanyName: organization.companyName || '',
			// 'update' is not offered at the moment — see OrganizationSection —
			// so every card starts out creating a new organization. The mode is
			// still honoured everywhere below, ready for it to come back.
			saveMode: 'create',
			linkedOrganizationId: null,
			isOriginal: true,
			domains: orgDomains.map((domain) => {
				const violations = organization.violations.filter(
					(v) => v.digitalPropertyId === domain.digitalPropertyId,
				)
				return {
					id: uuidv4(),
					name: domain.domainName,
					// Display only — used to explain what changing the name will do.
					// Deliberately not consulted by any save decision; saveMode alone
					// decides which organization is written to.
					loadedName: domain.domainName,
					// What the scope picker offers. Nothing below the host is fixed:
					// this is the domain everything else is split out of, so widening
					// it is as valid as narrowing it.
					...scopeSource(deepestViolation(violations), domain.domainName, false),
					violations,
					nameValidationError: null,
				}
			}),
			formValidationError: null,
			counterpartyType: organization.counterpartyType || '',
			companyNumber: organization.companyNumber || '',
			companyName: organization.companyName || '',
			address: organization.address || '',
			postalCode: organization.postalCode || '',
			city: organization.city || '',
			country: organization.country || '',
			emailAddresses: organization.emailAddresses
				? [...organization.emailAddresses]
				: [],
			bouncedEmailAddresses: organization.bouncedEmailAddresses
				? [...organization.bouncedEmailAddresses]
				: [],
			phoneNumbers: organization.phoneNumbers
				? organization.phoneNumbers.map((p) => ({ ...p }))
				: [],
		}

		// Find any violations not matched to a domain
		const matchedDigitalPropertyIds = new Set(orgDomains.map((d) => d.digitalPropertyId))
		const orphaned = organization.violations.filter(
			(v) => !matchedDigitalPropertyIds.has(v.digitalPropertyId),
		)

		this.state = {
			organizations: [initialOrg],
			orphanedViolations: orphaned,
			showCancelConfirm: false,
			saving: false,
		}
	}

	componentDidMount() {
		document.addEventListener('keydown', this.handleKeyDown)
		document.body.style.overflow = 'hidden'
	}

	componentWillUnmount() {
		document.removeEventListener('keydown', this.handleKeyDown)
		document.body.style.overflow = ''
	}

	handleKeyDown = (e) => {
		if (e.key === 'Escape') {
			this.handleCancel()
		}
	}

	handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			this.handleCancel()
		}
	}

	handleCancel = () => {
		this.setState({ showCancelConfirm: true })
	}

	handleCancelConfirm = () => {
		this.props.onClose()
	}

	handleCancelDismiss = () => {
		this.setState({ showCancelConfirm: false })
	}

	addOrganizationFromViolation = (violation) => {
		const { organization } = this.props
		const { organizations } = this.state

		// The domain the violation sits on today is what this card carves out of.
		const currentDomain = (organization.domains || []).find(
			(d) => d.digitalPropertyId === violation.digitalPropertyId,
		)

		const newOrg = {
			id: uuidv4(),
			organizationId: null,
			saveMode: 'create',
			linkedOrganizationId: null,
			domains: [
				domainFromViolation(
					violation,
					currentDomain ? currentDomain.domainName : '',
				),
			],
			formValidationError: null,
			counterpartyType: organization.counterpartyType || '',
			companyNumber: '',
			companyName: '',
			address: organization.address || '',
			postalCode: organization.postalCode || '',
			city: organization.city || '',
			country: organization.country || '',
			emailAddresses: organization.emailAddresses
				? [...organization.emailAddresses]
				: [],
			phoneNumbers: organization.phoneNumbers
				? organization.phoneNumbers.map((p) => ({ ...p }))
				: [],
		}

		const updatedOrgs = [...organizations, newOrg]
		const reassigned = this.reassignViolations(updatedOrgs)
		this.setState({ organizations: reassigned })
	}

	addOrganization = () => {
		const { organization } = this.props
		const { organizations } = this.state

		const newOrg = {
			id: uuidv4(),
			organizationId: null,
			saveMode: 'create',
			linkedOrganizationId: null,
			domains: [
				{
					id: uuidv4(),
					name: '',
					violations: [],
					nameValidationError: 'Domain name cannot be empty',
				},
			],
			formValidationError: null,
			counterpartyType: organization.counterpartyType || '',
			companyNumber: '',
			companyName: '',
			address: organization.address || '',
			postalCode: organization.postalCode || '',
			city: organization.city || '',
			country: organization.country || '',
			emailAddresses: organization.emailAddresses
				? [...organization.emailAddresses]
				: [],
			phoneNumbers: organization.phoneNumbers
				? organization.phoneNumbers.map((p) => ({ ...p }))
				: [],
		}

		this.setState({ organizations: [...organizations, newOrg] })
	}

	deleteOrganization = (orgId) => {
		const { organizations } = this.state
		const org = organizations.find((o) => o.id === orgId)

		// Check if org has any violations across its domains
		const totalViolations = org.domains.reduce(
			(sum, d) => sum + d.violations.length,
			0,
		)
		if (totalViolations > 0) {
			alert(
				'Cannot delete organization with violations. Please reassign them first.',
			)
			return
		}

		if (organizations.length === 1) {
			return
		}

		this.setState({
			organizations: organizations.filter((o) => o.id !== orgId),
		})
	}

	addDomainToOrg = (orgId) => {
		const { organizations } = this.state
		this.setState({
			organizations: organizations.map((o) => {
				if (o.id === orgId) {
					return {
						...o,
						domains: [
							...o.domains,
							{
								id: uuidv4(),
								name: '',
								violations: [],
								nameValidationError: 'Domain name cannot be empty',
							},
						],
					}
				}
				return o
			}),
		})
	}

	deleteDomainFromOrg = (orgId, digitalPropertyId) => {
		const { organizations } = this.state
		const org = organizations.find((o) => o.id === orgId)
		const domain = org.domains.find((d) => d.id === digitalPropertyId)

		if (domain.violations.length > 0) {
			alert('Cannot delete domain with violations. Please reassign them first.')
			return
		}

		if (org.domains.length === 1) {
			return
		}

		this.setState({
			organizations: organizations.map((o) => {
				if (o.id === orgId) {
					return {
						...o,
						domains: o.domains.filter((d) => d.id !== digitalPropertyId),
					}
				}
				return o
			}),
		})
	}

	updateDomainName = (orgId, digitalPropertyId, newName) => {
		const { organizations } = this.state

		// Collect all domain names across all orgs for duplicate check
		const allDomains = organizations.flatMap((o) => o.domains)

		// Validate
		let validationError = null
		if (!newName || newName.trim() === '') {
			validationError = 'Domain name cannot be empty'
		} else {
			const duplicateExists = allDomains.some(
				(d) => d.id !== digitalPropertyId && d.name === newName,
			)
			if (duplicateExists) {
				validationError = `Domain name "${newName}" already exists`
			}
		}

		// Update domain name
		const updatedOrgs = organizations.map((o) => {
			if (o.id === orgId) {
				return {
					...o,
					domains: o.domains.map((d) =>
						d.id === digitalPropertyId
							? { ...d, name: newName, nameValidationError: validationError }
							: d,
					),
				}
			}
			return o
		})

		// Reassign violations
		const reassigned = this.reassignViolations(updatedOrgs)
		this.setState({ organizations: reassigned })
	}

	reassignViolations = (organizations) => {
		const { organization } = this.props
		const allViolations = organization.violations

		// Collect all domains across all orgs
		const allDomains = organizations.flatMap((o) =>
			o.domains.map((d) => ({ ...d, orgId: o.id })),
		)

		const violationAssignments = new Map()
		const orphaned = []

		allViolations.forEach((violation) => {
			const violationUrl = violation.urlRaw || violation.url || ''
			let bestMatch = null
			let longestMatchLength = 0

			allDomains.forEach((domain) => {
				if (
					violationUrl.includes(domain.name) &&
					domain.name.length > longestMatchLength
				) {
					bestMatch = domain
					longestMatchLength = domain.name.length
				}
			})

			if (bestMatch) {
				const key = `${bestMatch.orgId}:${bestMatch.id}`
				if (!violationAssignments.has(key)) {
					violationAssignments.set(key, [])
				}
				violationAssignments.get(key).push(violation)
			} else {
				orphaned.push(violation)
			}
		})

		this.setState({ orphanedViolations: orphaned })

		return organizations.map((o) => ({
			...o,
			domains: o.domains.map((d) => ({
				...d,
				violations: violationAssignments.get(`${o.id}:${d.id}`) || [],
			})),
		}))
	}

	updateOrgField = (orgId, field, value) => {
		const { organizations } = this.state
		this.setState({
			organizations: organizations.map((o) =>
				o.id === orgId ? { ...o, [field]: value } : o,
			),
		})
	}

	updateOrgArrayField = (orgId, field, index, value) => {
		const { organizations } = this.state
		this.setState({
			organizations: organizations.map((o) => {
				if (o.id === orgId) {
					const array = [...(o[field] || [])]
					array[index] = value
					return { ...o, [field]: array }
				}
				return o
			}),
		})
	}

	addOrgArrayField = (orgId, field) => {
		const { organizations } = this.state
		this.setState({
			organizations: organizations.map((o) => {
				if (o.id === orgId) {
					let newValue = ''
					if (field === 'phoneNumbers') {
						const callingCode = this.getCallingCode(o.country)
						newValue = { prefix: callingCode || '', phoneNumber: '' }
					}
					const array = [...(o[field] || []), newValue]
					return { ...o, [field]: array }
				}
				return o
			}),
		})
	}

	removeOrgArrayField = (orgId, field, index) => {
		const { organizations } = this.state
		this.setState({
			organizations: organizations.map((o) => {
				if (o.id === orgId) {
					const array = [...(o[field] || [])]
					array.splice(index, 1)
					return { ...o, [field]: array }
				}
				return o
			}),
		})
	}

	updateOrgPhoneField = (orgId, index, field, value) => {
		const { organizations } = this.state
		this.setState({
			organizations: organizations.map((o) => {
				if (o.id === orgId) {
					const phoneNumbers = [...(o.phoneNumbers || [])]
					phoneNumbers[index] = { ...phoneNumbers[index], [field]: value }
					return { ...o, phoneNumbers }
				}
				return o
			}),
		})
	}

	handleOrgValidationChange = (orgId, hasErrors) => {
		const { organizations } = this.state
		const errorMessage = hasErrors ? 'Fix email/phone validation errors' : null
		this.setState({
			organizations: organizations.map((o) =>
				o.id === orgId ? { ...o, formValidationError: errorMessage } : o,
			),
		})
	}

	// Switch what this card does on save. Leaving 'link' drops the linked
	// organization, so the choice can't silently outlive the mode.
	updateSaveMode = (orgId, saveMode) => {
		const { organizations } = this.state
		this.setState({
			organizations: organizations.map((o) =>
				o.id === orgId
					? {
							...o,
							saveMode,
							linkedOrganizationId:
								saveMode === 'link' ? o.linkedOrganizationId : null,
						}
					: o,
			),
		})
	}

	handleOrganizationFound = (orgId, org) => {
		const { organizations } = this.state
		this.setState({
			organizations: organizations.map((o) => {
				if (o.id === orgId) {
					return {
						...o,
						saveMode: 'link',
						linkedOrganizationId: org.organizationId,
						companyName: org.companyName || o.companyName,
						companyNumber: org.companyNumber || o.companyNumber,
						address: org.address || o.address,
						postalCode: org.postalCode || o.postalCode,
						city: org.city || o.city,
						country: org.country || o.country,
						counterpartyType: org.counterpartyType || o.counterpartyType,
						emailAddresses:
							org.emailAddresses && org.emailAddresses.length > 0
								? [...org.emailAddresses]
								: o.emailAddresses,
						bouncedEmailAddresses: org.bouncedEmailAddresses || [],
						phoneNumbers:
							org.phoneNumbers && org.phoneNumbers.length > 0
								? org.phoneNumbers.map((p) => ({ ...p }))
								: o.phoneNumbers,
					}
				}
				return o
			}),
		})
	}

	handleOrgFormValidChange = (orgId, isValid, reason) => {
		const { organizations } = this.state
		const errorMessage = !isValid
			? reason || 'Complete all required fields'
			: null
		this.setState({
			organizations: organizations.map((o) => {
				if (o.id === orgId) {
					if (o.formValidationError === 'Fix email/phone validation errors') {
						return o
					}
					return { ...o, formValidationError: errorMessage }
				}
				return o
			}),
		})
	}

	getCallingCode = (countryCode) => {
		const market = markets.find((m) => m.code === countryCode)
		return market ? market.phoneNumberCode : ''
	}

	validateSave = () => {
		const { organizations } = this.state
		const errors = []

		organizations.forEach((org, orgIndex) => {
			// A card the user added needs cases to move, otherwise it would leave
			// an empty organization behind. The card the modal was opened on may
			// end up with none — that is exactly what splitting the last case out
			// to a branch leaves behind, and it is then skipped in buildPayload.
			if (!org.isOriginal) {
				org.domains.forEach((domain, domainIndex) => {
					if (domain.violations.length === 0) {
						errors.push(
							`Organization ${orgIndex + 1}, Domain ${domainIndex + 1} (${domain.name || 'unnamed'}) has no violations`,
						)
					}
				})
			}

			if (org.saveMode === 'link' && !org.linkedOrganizationId) {
				errors.push(
					`Organization ${orgIndex + 1} is set to link to an existing organization, but none has been selected`,
				)
			}

			if (org.saveMode === 'update' && !org.organizationId) {
				errors.push(
					`Organization ${orgIndex + 1} has no existing organization to update`,
				)
			}

			if (org.formValidationError) {
				errors.push(`Organization ${orgIndex + 1}: ${org.formValidationError}`)
			}

			// Check domain name errors
			org.domains.forEach((domain, domainIndex) => {
				if (domain.nameValidationError) {
					errors.push(
						`Organization ${orgIndex + 1}, Domain ${domainIndex + 1}: ${domain.nameValidationError}`,
					)
				}
			})
		})

		// Check for duplicate domain names across all orgs
		const allNames = organizations.flatMap((o) => o.domains.map((d) => d.name))
		const duplicates = allNames.filter(
			(name, index) => name && allNames.indexOf(name) !== index,
		)
		if (duplicates.length > 0) {
			errors.push(
				`Duplicate domain names: ${[...new Set(duplicates)].join(', ')}`,
			)
		}

		return errors
	}

	// Build one payload entry. `contact` supplies the organization fields,
	// `organizationId` decides create-vs-update on the server, and `domains`
	// is the subset of domains this entry owns.
	buildPayloadEntry = (contact, organizationId, domains) => {
		const emailAddresses = new Set(
			(contact.emailAddresses || []).filter(
				(emailAddress) => emailAddress && emailAddress.trim() !== '',
			),
		)

		const phoneNumbers = new Set()
		;(contact.phoneNumbers || []).forEach((phone) => {
			if (
				phone.phoneNumber &&
				phone.phoneNumber.trim() !== '' &&
				phone.prefix &&
				phone.prefix.trim() !== ''
			) {
				try {
					const fullNumber = `${phone.prefix.trim()}${phone.phoneNumber.trim()}`
					const parsedPhone = parsePhoneNumberWithError(fullNumber)
					if (parsedPhone.isValid()) {
						phoneNumbers.add(parsedPhone.format('E.164'))
					}
				} catch (error) {
					console.warn('Failed to parse phone number:', phone, error)
				}
			}
		})

		return {
			organizationId: organizationId || null,
			counterpartyType: contact.counterpartyType,
			companyNumber: contact.companyNumber || null,
			companyName: contact.companyName,
			address: contact.address || null,
			postalCode: contact.postalCode || null,
			city: contact.city || null,
			country: contact.country || null,
			emailAddresses: Array.from(emailAddresses).map((email) =>
				email.toLowerCase().trim(),
			),
			phoneNumbers: Array.from(phoneNumbers),
			domains: domains.map((domain) => ({
				domainName: domain.name,
				violationIds: domain.violations.map((v) => v.violationId),
				violations: domain.violations.map((v) => ({
					violationId: v.violationId,
					violationType: v.violationType,
					allocationNotes: v.allocationNotes || null,
				})),
			})),
		}
	}

	// Each card's saveMode maps 1:1 onto what the server does with it:
	//   update — write back to the organization this card was loaded from
	//   create — insert a new organization and move this card's domains to it
	//   link   — retarget this card's domains onto the organization the user
	//            linked by company number
	// Nothing else about the card's state changes this; editing a domain name
	// or contact details never reassigns which organization is written to.
	organizationIdForSave = (org) => {
		if (org.saveMode === 'update') {
			return org.organizationId || null
		}
		if (org.saveMode === 'link') {
			return org.linkedOrganizationId || null
		}
		return null
	}

	buildPayload = () => {
		const { organizations } = this.state

		return (
			organizations
				// A card holding no cases has nothing to move, so there is nothing to
				// create or link it to. This is the card the modal was opened on once
				// its last case has been split out — sending it would only duplicate
				// the organization it came from.
				.filter((org) =>
					org.domains.some((domain) => domain.violations.length > 0),
				)
				.map((org) =>
					this.buildPayloadEntry(
						org,
						this.organizationIdForSave(org),
						org.domains,
					),
				)
		)
	}

	handleSave = async () => {
		const errors = this.validateSave()

		if (errors.length > 0) {
			alert('Cannot save:\n\n' + errors.join('\n'))
			return
		}

		this.setState({ saving: true })

		try {
			const payload = this.buildPayload()

			await createOrganizations(payload)

			this.setState({ saving: false })

			if (this.props.onSave) {
				await this.props.onSave()
			}

			this.props.onClose()
		} catch (error) {
			console.error('Error saving organization split:', error)
			alert('Failed to save: ' + (error.message || 'Unknown error'))
		} finally {
			this.setState({ saving: false })
		}
	}

	getValidationErrorMessage = () => {
		const { organizations, orphanedViolations } = this.state

		if (orphanedViolations.length > 0) {
			return `${orphanedViolations.length} orphaned violation${orphanedViolations.length > 1 ? 's' : ''} must be assigned to a domain`
		}

		// Check domain name errors
		for (const org of organizations) {
			for (const domain of org.domains) {
				if (domain.nameValidationError) {
					return `"${domain.name || 'unnamed'}" has an invalid name: ${domain.nameValidationError}`
				}
			}
		}

		// Check form validation errors
		const orgWithFormError = organizations.find(
			(o) => o.formValidationError !== null,
		)
		if (orgWithFormError) {
			return `"${orgWithFormError.companyName || 'Organization'}": ${orgWithFormError.formValidationError}`
		}

		// A card set to "link" needs an organization picked by company number
		const unlinked = organizations.find(
			(o) => o.saveMode === 'link' && !o.linkedOrganizationId,
		)
		if (unlinked) {
			return `"${unlinked.companyName || 'Organization'}" is set to link to an existing organization — search by organisation number to pick one`
		}

		// Check empty domains (the card the modal was opened on may be emptied)
		for (const org of organizations) {
			if (org.isOriginal) continue
			for (const domain of org.domains) {
				if (domain.violations.length === 0) {
					return `"${domain.name || 'unnamed'}" must have at least one violation`
				}
			}
		}

		// Check duplicate domain names
		const allNames = organizations.flatMap((o) => o.domains.map((d) => d.name))
		const duplicateName = allNames.find(
			(name, index) => name && allNames.indexOf(name) !== index,
		)
		if (duplicateName) {
			return `Duplicate domain name: "${duplicateName}"`
		}

		// Check duplicate company numbers
		const companyNums = organizations
			.map((o) => o.companyNumber && o.companyNumber.trim())
			.filter(Boolean)
		const duplicateCompanyNumber = companyNums.find(
			(num, index) => companyNums.indexOf(num) !== index,
		)
		if (duplicateCompanyNumber) {
			return `Company number "${duplicateCompanyNumber}" is used by multiple organizations. Each organization must have a unique company number.`
		}

		return ''
	}

	render() {
		const { organization } = this.props
		const { organizations, orphanedViolations, showCancelConfirm, saving } =
			this.state

		const hasOrphanedViolations = orphanedViolations.length > 0

		const hasNameValidationErrors = organizations.some((o) =>
			o.domains.some((d) => d.nameValidationError !== null),
		)

		const hasFormValidationErrors = organizations.some(
			(o) => o.formValidationError !== null,
		)

		const hasEmptyDomains = organizations.some(
			(o) =>
				!o.isOriginal && o.domains.some((d) => d.violations.length === 0),
		)

		const hasUnresolvedLink = organizations.some(
			(o) => o.saveMode === 'link' && !o.linkedOrganizationId,
		)

		const allNames = organizations.flatMap((o) => o.domains.map((d) => d.name))
		const hasDuplicateNames = allNames.some(
			(name, index) => name && allNames.indexOf(name) !== index,
		)

		const companyNumbers = organizations
			.map((o) => o.companyNumber && o.companyNumber.trim())
			.filter(Boolean)
		const hasDuplicateCompanyNumbers = companyNumbers.some(
			(num, index) => companyNumbers.indexOf(num) !== index,
		)

		const isSaveDisabled =
			saving ||
			hasOrphanedViolations ||
			hasNameValidationErrors ||
			hasFormValidationErrors ||
			hasEmptyDomains ||
			hasUnresolvedLink ||
			hasDuplicateNames ||
			hasDuplicateCompanyNumbers

		const modalRoot =
			document.getElementById('modal-root') ||
			(() => {
				const el = document.createElement('div')
				el.id = 'modal-root'
				document.body.appendChild(el)
				return el
			})()

		return ReactDOM.createPortal(
			<div
				role='dialog'
				aria-modal='true'
				className='domain-split-overlay'
				onClick={this.handleOverlayClick}
			>
				<div className='domain-split-modal'>
					{/* Header */}
					<div className='domain-split-header'>
						<h2 className='domain-split-title'>
							Edit Organization: {organization.companyName || 'Organization'}
						</h2>
						<button
							aria-label='Close'
							onClick={this.handleCancel}
							className='domain-split-close'
						>
							×
						</button>
					</div>

					{/* Content */}
					<div className='domain-split-content'>
						{/* Orphaned Violations Warning */}
						{orphanedViolations.length > 0 && (
							<div className='orphaned-violations-warning'>
								<div className='orphaned-violations-header'>
									<strong>
										Orphaned Violations ({orphanedViolations.length})
									</strong>
									<p>
										The following violations don't match any domain. Update your
										domain names to include these violations.
									</p>
								</div>
								<div className='orphaned-violations-list'>
									{orphanedViolations.map((violation, index) => (
										<div key={index} className='orphaned-violation-item'>
											<span className='orphaned-violation-url'>
												{violation.urlRaw || violation.url}
											</span>
										</div>
									))}
								</div>
							</div>
						)}

						{organizations.map((org, index) => (
							<OrganizationSection
								key={org.id}
								org={org}
								index={index}
								canDelete={organizations.length > 1}
								onDomainNameChange={(digitalPropertyId, name) =>
									this.updateDomainName(org.id, digitalPropertyId, name)
								}
								onAddDomain={() => this.addDomainToOrg(org.id)}
								onDeleteDomain={(digitalPropertyId) =>
									this.deleteDomainFromOrg(org.id, digitalPropertyId)
								}
								onDelete={() => this.deleteOrganization(org.id)}
								onFieldChange={(field, value) =>
									this.updateOrgField(org.id, field, value)
								}
								onArrayFieldChange={(field, index, value) =>
									this.updateOrgArrayField(org.id, field, index, value)
								}
								onAddArrayField={(field) =>
									this.addOrgArrayField(org.id, field)
								}
								onRemoveArrayField={(field, index) =>
									this.removeOrgArrayField(org.id, field, index)
								}
								onPhoneFieldChange={(index, field, value) =>
									this.updateOrgPhoneField(org.id, index, field, value)
								}
								onValidationChange={(hasErrors) =>
									this.handleOrgValidationChange(org.id, hasErrors)
								}
								onFormValidChange={(isValid, reason) =>
									this.handleOrgFormValidChange(org.id, isValid, reason)
								}
								onOrganizationFound={(foundOrg) =>
									this.handleOrganizationFound(org.id, foundOrg)
								}
								onSaveModeChange={(mode) => this.updateSaveMode(org.id, mode)}
								onAddOrganizationFromViolation={
									this.addOrganizationFromViolation
								}
							/>
						))}

						{/* Add Organization Button */}
						<button
							className='add-subdomain-button'
							onClick={this.addOrganization}
						>
							+ Add Organization
						</button>
					</div>

					{/* Footer */}
					<div className='domain-split-footer'>
						{isSaveDisabled && !saving && (
							<div className='save-error-text'>
								{this.getValidationErrorMessage()}
							</div>
						)}
						<div className='save-button-container'>
							<button
								className='domain-split-button cancel-button'
								onClick={this.handleCancel}
								disabled={saving}
							>
								Cancel
							</button>
							<button
								className='domain-split-button save-button'
								onClick={this.handleSave}
								disabled={isSaveDisabled}
							>
								{saving ? 'Saving...' : 'Save'}
							</button>
						</div>
					</div>
				</div>

				{/* Cancel Confirmation Dialog */}
				{showCancelConfirm && (
					<div className='confirm-overlay'>
						<div className='confirm-dialog'>
							<h3>Discard Changes?</h3>
							<p>Are you sure? All changes will be lost.</p>
							<div className='confirm-actions'>
								<button
									className='confirm-button secondary'
									onClick={this.handleCancelDismiss}
								>
									Continue Editing
								</button>
								<button
									className='confirm-button primary'
									onClick={this.handleCancelConfirm}
								>
									Discard Changes
								</button>
							</div>
						</div>
					</div>
				)}
			</div>,
			modalRoot,
		)
	}
}

DigitalPropertySplitModal.propTypes = {
	organization: PropTypes.object.isRequired,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func,
}

export default DigitalPropertySplitModal
